import {
  Task,
  defModule,
  type TaskDeclaration,
  type TaskMessage,
  type UpdateResult
} from '@st/redux'
import type { SDKResponseFor } from '@st/sdk'
import { Effect } from 'effect'
import { create } from 'mutative'
import { match, P } from '@st/pattern'
import { deepEqual } from '@st/util/deep-equal'
import { orderedOrganizerSections } from './st-folder-selectors'
import { SdkClient } from '~/effects'

type Settings = SDKResponseFor<'folders/getClientOrganizerSettings'>

type State =
  | { status: 'idle' }
  | { status: 'loading'; folderId: string; returnType: string }
  | {
      status: 'loaded' | 'saving'
      initialSettings: Settings
      returnType: string
      folderId: string
      settings: Settings
    }

type Message =
  | { type: 'open'; folderId: string; returnType: string }
  | { type: 'close' }
  | { type: 'change'; section: string; enabled: boolean }
  | { type: 'save' }
  | { type: 'saved' }
  | TaskMessage<typeof load>

const load = Task.define((folderId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const data = yield* sdk.send({ type: 'folders/getClientOrganizerSettings', folderId })
    return { type: 'loaded', data } as const
    // No failure has a product response yet, so an unexpected one is a defect.
  }).pipe(Effect.orDie)
)

export const folderOrganizerSettingsModule = defModule<{ state: State; message: Message }>({
  init: () => ({ status: 'idle' }),
  tasks: (state) =>
    match(state)
      .with({ status: 'loading' }, ({ folderId }) => [load(folderId)])
      .otherwise(() => []),
  update: (state, message) =>
    match({ state, message })
      .returnType<UpdateResult<State, Message>>()
      .with({ state: { status: 'saving' }, message: { type: 'saved' } }, () => ({ status: 'idle' }))
      // Escape and backdrop clicks can still send close while buttons are disabled.
      // Keep the dialog open until the accepted save completes.
      .with({ state: { status: 'saving' } }, ({ state }) => state)
      .with({ message: { type: 'open' } }, ({ message }) => ({
        status: 'loading',
        folderId: message.folderId,
        returnType: message.returnType
      }))
      .with({ message: { type: 'close' } }, () => ({ status: 'idle' }))
      .with(
        { state: { status: 'loading' }, message: { type: 'loaded' } },
        ({ state, message }) => ({
          ...state,
          status: 'loaded',
          initialSettings: message.data,
          settings: message.data
        })
      )
      .with({ state: { status: 'loaded' }, message: { type: 'change' } }, ({ state, message }) =>
        create(state, (draft) => {
          draft.settings.sections[message.section].enabled = message.enabled
        })
      )
      .with({ state: { status: 'loaded' }, message: { type: 'save' } }, ({ state }) => [
        { ...state, status: 'saving' },
        Effect.gen(function* () {
          const sdk = yield* SdkClient
          yield* sdk.send({
            type: 'folders/updateClientOrganizerSettings',
            folderId: state.folderId,
            settings: { sections: state.settings.sections }
          })
          return { type: 'saved' } as const
          // No failure has a product response yet, so an unexpected one is a defect.
        }).pipe(Effect.orDie)
      ])
      .otherwise(() => {
        throw new Error(
          `Unexpected organizer settings message ${message.type} while ${state.status}`
        )
      })
})

export function selOrganizerSettings(state: State) {
  return match(state)
    .with({ status: P.union('idle', 'loading') }, () => ({
      edited: false,
      canSave: false,
      willOverride: false,
      sections: []
    }))
    .with({ status: P.union('loaded', 'saving') }, ({ status, settings, initialSettings }) => {
      const edited = !deepEqual(settings, initialSettings)
      return {
        edited,
        canSave: status == 'loaded' && edited,
        willOverride: edited && settings.source == 'organization',
        sections: orderedOrganizerSections(settings)
      }
    })
    .exhaustive()
}
